import { AlertTriangle, Coins } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTokens } from '../../hooks';

interface TokensProgressBarProps {
  className?: string;
  showWarning?: boolean;
  warningThreshold?: number;
}

export const TokensProgressBar: React.FC<TokensProgressBarProps> = ({
  className = '',
  showWarning = true,
  warningThreshold = 10,
}) => {
  const { t } = useTranslation();
  const { tokensBalance, tokensTotal } = useTokens();

  // Calcular porcentagem baseada em balance / total
  const percentage = useMemo(() => {
    // Se não houver total definido, usar fallback
    if (tokensTotal === 0 || !tokensTotal) {
      // Se houver balance mas não total, mostrar 100% (assumindo que já usou tudo)
      return tokensBalance > 0 ? 100 : 0;
    }
    
    // Se balance for maior que total (caso edge), mostrar 100%
    if (tokensBalance > tokensTotal) {
      return 100;
    }
    
    // Calcular porcentagem: (balance / total) * 100
    return (tokensBalance / tokensTotal) * 100;
  }, [tokensBalance, tokensTotal]);

  // Calcular thresholds baseados em porcentagem do total
  const lowThresholdPercentage = tokensTotal > 0 ? (warningThreshold / tokensTotal) * 100 : 0;
  const criticalThresholdPercentage = tokensTotal > 0 ? (5 / tokensTotal) * 100 : 0;
  
  const isLow = tokensTotal > 0 ? percentage <= lowThresholdPercentage : tokensBalance <= warningThreshold;
  const isEmpty = tokensBalance === 0;

  // Cores baseadas no estado
  const getColorClasses = () => {
    if (isEmpty) {
      return 'bg-red-500';
    }
    if (isLow) {
      return 'bg-yellow-500';
    }
    return 'bg-green-500';
  };

  const getTextColor = () => {
    if (isEmpty) {
      return 'text-red-600 dark:text-red-400';
    }
    if (isLow) {
      return 'text-yellow-600 dark:text-yellow-400';
    }
    return 'text-green-600 dark:text-green-400';
  };

  const [showTooltip, setShowTooltip] = useState(false);

  const formatTokens = (tokens: number) => {
    // Mostrar valores inteiros com separador de milhar para facilitar leitura
    return tokens.toLocaleString('pt-BR', { maximumFractionDigits: 0 });
  };

  // Tooltip com informações completas
  const tooltipContent = tokensTotal > 0
    ? `${t('tokens.currentBalance')}: ${formatTokens(tokensBalance)} / ${formatTokens(tokensTotal)} ${t('tokens.tokens')} (${percentage.toFixed(1)}% restante)${isLow && showWarning ? ` - ${t('tokens.lowBalanceWarning')}` : ''}`
    : `${t('tokens.currentBalance')}: ${formatTokens(tokensBalance)} ${t('tokens.tokens')}${isLow && showWarning ? ` - ${t('tokens.lowBalanceWarning')}` : ''}`;

  return (
    <div className={`flex items-center space-x-2 ${className}`}>
      <div className="relative flex-1">
        <div
          className="flex items-center space-x-2 px-3 py-1.5 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer"
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
          title={tooltipContent}
        >
        {/* Ícone */}
        <Coins
          className={`h-4 w-4 ${getTextColor()}`}
        />

        {/* Barra de progresso */}
        <div className="flex-1 min-w-[80px] h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-300 ${getColorClasses()}`}
            style={{ width: `${Math.max(percentage, 2)}%` }}
          />
        </div>

        {/* Valor */}
        <div className={`text-xs font-semibold ${getTextColor()} min-w-[35px] text-right`}>
          {formatTokens(tokensBalance)}
        </div>

          {/* Ícone de aviso se estiver baixo */}
          {isLow && showWarning && (
            <AlertTriangle className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
          )}
        </div>

        {/* Tooltip customizado */}
        {showTooltip && (
          <div className="absolute z-50 top-full left-1/2 transform -translate-x-1/2 mt-2 px-3 py-2 text-xs text-white bg-gray-900 dark:bg-gray-700 rounded-lg shadow-lg whitespace-nowrap pointer-events-none">
            <div className="font-semibold mb-1">
              {t('tokens.currentBalance')}
            </div>
            {tokensTotal > 0 ? (
              <>
                <div>{formatTokens(tokensBalance)} / {formatTokens(tokensTotal)} {t('tokens.tokens')}</div>
                <div className="mt-1 text-gray-300">
                  {percentage.toFixed(1)}% restante
                </div>
              </>
            ) : (
              <div>{formatTokens(tokensBalance)} {t('tokens.tokens')}</div>
            )}
            {isLow && showWarning && (
              <div className="mt-2 text-yellow-300 text-xs">
                {t('tokens.lowBalanceWarning')}
              </div>
            )}
            {/* Arrow */}
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-b-gray-900 dark:border-b-gray-700" />
          </div>
        )}
      </div>
    </div>
  );
};
